import LoadingIcons from 'react-loading-icons';
import { useCalculateCurrentSingleProfitCalculationQuery } from '../../../../store/services/api';
import { colors } from '../../../../colors';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ProfitInformation from '../../../ProfitCalculations/Content/SingleProfitCalculation/Result/ProfitInformation';
import SummaryChart from './Chart';
import ScrollIndicator from './ScrollIndicator';

interface Props {
  baseAsset: string;
  setActiveAsset: React.Dispatch<
    React.SetStateAction<string | boolean | undefined>
  >;
}

// Don't render in parent if bot is not active
const CalculationSummary = ({ baseAsset, setActiveAsset }: Props) => {
  const { data: spcData, isLoading } =
    useCalculateCurrentSingleProfitCalculationQuery(baseAsset);

  const scrollableRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  const activeAsset = useMemo(() => {
    if (!spcData?.data) {
      return false;
    }

    return spcData.data.latestOrder === 'BUY'
      ? spcData.data.baseAsset
      : spcData.data.quoteAsset;
  }, [spcData?.data]);

  useEffect(() => {
    setActiveAsset(activeAsset);
  }, [activeAsset, setActiveAsset]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 500); // 500ms delay

    return () => clearTimeout(timer); // Cleanup on unmount
  }, []);

  useLayoutEffect(() => {
    if (!scrollableRef.current || !spcData?.data || !isReady) {
      return;
    }

    const handleScroll = () => {
      if (!scrollableRef.current) {
        return;
      }

      // Stop animation after first scroll
      setHasScrolled(true);
      // Remove listener to prevent further checks
      scrollableRef.current.removeEventListener('scroll', handleScroll);
    };

    const scrollableDiv = scrollableRef.current;
    scrollableDiv.addEventListener('scroll', handleScroll);

    return () => {
      scrollableDiv.removeEventListener('scroll', handleScroll);
    };
  }, [isReady, spcData?.data]);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          width: '100%',
          opacity: isReady ? 1 : 0,
          transition: 'opacity 0.5s',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            columnGap: 8,
          }}
        >
          <LoadingIcons.Grid width={16} height={16} color={colors.orange} />
          <p
            style={{
              margin: 0,
              color: colors.orange,
              fontSize: 18,
              fontWeight: 'bold',
            }}
          >
            Loading Calculations...
          </p>
        </div>
        <p
          style={{
            margin: 0,
            color: colors.orange,
            fontSize: 18,
            fontWeight: 'bold',
          }}
        >
          This can take up to 10 seconds.
        </p>
      </div>
    );
  }

  if (!spcData || !isReady) {
    return null;
  }

  if (!spcData?.data) {
    return (
      <span
        style={{
          margin: 0,
          color: colors.orange,
          fontSize: 18,
          fontWeight: 'bold',
        }}
      >
        Current profit calculations are still unknown.
      </span>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        width: '100%',
        overflowY: 'scroll',
        position: 'relative',
      }}
      className="no-scrollbar"
      ref={scrollableRef}
    >
      <div
        style={{
          height: '100%',
        }}
      >
        <SummaryChart data={spcData.data} />
        <ScrollIndicator hasScrolled={hasScrolled} />
      </div>
      <div
        style={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          overflowY: 'scroll',
        }}
      >
        <ProfitInformation data={spcData.data} />
      </div>
    </div>
  );
};

export default CalculationSummary;
